import React, { useState } from 'react';
import {
    deleteInvoicePaymentMethod,
    setDefaultInvoicePaymentMethod,
} from '../../services/paymentsService';

const BRAND_LABEL = {
    VISA: 'Visa',
    MASTERCARD: 'Mastercard',
    AMERICAN_EXPRESS: 'Amex',
    AMEX: 'Amex',
    DISCOVER: 'Discover',
    JCB: 'JCB',
    DINERS: 'Diners',
    UNIONPAY: 'UnionPay',
};

// Patient-facing card-type label. Rainforest distinguishes CREDIT,
// DEBIT, PREPAID, UNKNOWN — we collapse PREPAID into "Card" because
// most patients don't think of HSA/FSA as a class apart, and UNKNOWN
// is not displayed at all (we'd be lying about a card we couldn't
// classify).
const TYPE_LABEL = {
    CREDIT: 'Credit',
    DEBIT: 'Debit',
};

const cardLabel = (card = {}) => {
    // Prefer Rainforest's friendly ``brand_desc`` ("Mastercard") when
    // present; fall back to mapping the machine ``brand`` enum, then to a
    // generic label so we never render literally "undefined".
    const brand =
        card.brandDesc
        || BRAND_LABEL[card.brand]
        || (card.brand ? card.brand.toString().toLowerCase().replace(/_/g, ' ') : 'Card');
    const last4 = card.last4 ? ` •• ${card.last4}` : '';
    return `${brand}${last4}`;
};

const cardTypeLabel = (card = {}) => {
    const t = (card.type || '').toUpperCase();
    return TYPE_LABEL[t] || null;
};

const expiryLabel = (card = {}) => {
    if (!card.expirationMonth || !card.expirationYear) return null;
    const mm = String(card.expirationMonth).padStart(2, '0');
    const yy = String(card.expirationYear).slice(-2);
    return `Exp ${mm}/${yy}`;
};

const formatCurrencyCents = (cents) => {
    if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(cents / 100);
};

/**
 * SavedCardList
 * =============
 *
 * Renders the patient's saved cards as compact pill rows with a default
 * star and a delete control. The "Add new card" button surfaces the
 * AddPaymentMethod component (opened in a modal/inline by the parent).
 *
 * The component is deliberately presentational + delegating: it receives
 * the rows from the parent (which loaded them via
 * `listInvoicePaymentMethods`) and re-fetches via `onChanged` after any
 * mutation so the parent owns the source of truth.
 *
 * Props:
 *   publicHash, gateToken — required for the delete API call
 *   methods               — array of `{id, isDefault, card, ...}` rows
 *   onAddNew              — opens the add-card UI
 *   onChanged             — called after a successful delete so the
 *                           parent can reload the list
 *   onChargeCardOnFile    — optional Phase 3 hook: when present, an extra
 *                           "Charge this card" button is rendered. Undefined
 *                           in Phase 2.
 */
export default function SavedCardList({
    publicHash,
    gateToken,
    methods,
    onAddNew,
    onChanged,
    onChargeCardOnFile,
    chargingVaultId,
}) {
    const [deletingId, setDeletingId] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [defaultingId, setDefaultingId] = useState(null);
    const [error, setError] = useState(null);

    const handleDelete = async (vaultId) => {
        setDeletingId(vaultId);
        setError(null);
        try {
            const { ok, data } = await deleteInvoicePaymentMethod(publicHash, gateToken, vaultId);
            if (!ok || !data?.success) {
                setError(data?.error || 'Could not remove this card.');
                return;
            }
            setConfirmId(null);
            onChanged?.();
        } finally {
            setDeletingId(null);
        }
    };

    const handleMakeDefault = async (vaultId) => {
        setDefaultingId(vaultId);
        setError(null);
        try {
            const { ok, data } = await setDefaultInvoicePaymentMethod(publicHash, gateToken, vaultId);
            if (!ok || !data?.success) {
                setError(data?.error || 'Could not update your default card.');
                return;
            }
            // Server returns the freshly-reconciled wallet; we still
            // bubble through onChanged so the parent re-fetches and
            // reuses its existing surcharge-preview enrichment path.
            onChanged?.();
        } finally {
            setDefaultingId(null);
        }
    };

    const empty = !methods || methods.length === 0;

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e0e6ed',
            borderRadius: 8,
            padding: 'clamp(16px, 4vw, 24px)',
            marginBottom: 16,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 16 }}>Saved payment methods</div>
                {!empty && (
                    <button type="button" onClick={onAddNew} style={linkButton}>
                        + Add new card
                    </button>
                )}
            </div>

            {empty && (
                <div>
                    <div style={{ color: '#586473', fontSize: 14, marginBottom: 12 }}>
                        You don't have any cards on file yet.
                    </div>
                    <button type="button" onClick={onAddNew} style={primaryButton}>
                        Add a card
                    </button>
                </div>
            )}

            {!empty && methods.map((m) => {
                const isDeleting = deletingId === m.id;
                const isConfirming = confirmId === m.id;
                const isDefaulting = defaultingId === m.id;
                const exp = expiryLabel(m.card);
                const typeLabel = cardTypeLabel(m.card);
                const preview = m.surchargePreview;
                // The button label drops the actual total in for surcharged
                // credit cards so the patient never has to math the % to
                // understand what they're authorising. Debit (and the
                // no-preview legacy path) fall back to the existing copy.
                let chargeButtonLabel = 'Pay with this card';
                if (preview && preview.kind === 'surcharge') {
                    const total = formatCurrencyCents(preview.totalCents);
                    if (total) chargeButtonLabel = `Pay ${total}`;
                } else if (preview && typeof preview.totalCents === 'number') {
                    const total = formatCurrencyCents(preview.totalCents);
                    if (total) chargeButtonLabel = `Pay ${total}`;
                }
                return (
                    <div
                        key={m.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            border: '1px solid #e0e6ed',
                            borderRadius: 6,
                            marginBottom: 8,
                            backgroundColor: m.isDefault ? '#f3f8ff' : '#fff',
                        }}
                    >
                        <div style={{ fontSize: 18, lineHeight: 1, color: '#1976d2', minWidth: 16 }}>
                            {m.isDefault ? '★' : '☆'}
                        </div>
                        {/* textAlign:left is explicit because the public
                            invoice page centers its column content; without
                            it the non-flex lines (brand, surcharge note)
                            inherit center alignment while the flex row
                            (metadata) stays left, producing a zig-zag. */}
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                                fontWeight: 500,
                                fontSize: 14,
                            }}>
                                <span>{cardLabel(m.card)}</span>
                                {typeLabel && (
                                    <span style={typeBadge(typeLabel)}>{typeLabel}</span>
                                )}
                            </div>
                            <div style={{
                                color: '#7c8a9a',
                                fontSize: 12,
                                marginTop: 2,
                            }}>
                                {[exp, m.isDefault ? 'Default' : null].filter(Boolean).join(' · ')}
                            </div>
                            {/* Surcharge preview — total is on the Pay
                                button alongside, so we only surface the
                                rate here. */}
                            {preview && preview.kind === 'surcharge' && (
                                <div style={surchargeNote}>
                                    + {preview.surchargePct}% surcharge on credit
                                </div>
                            )}
                        </div>
                        {!isConfirming && !m.isDefault && (
                            <button
                                type="button"
                                onClick={() => handleMakeDefault(m.id)}
                                style={ghostButton}
                                disabled={isDefaulting || !!defaultingId || isDeleting}
                                title="Use this card by default for future payments"
                            >
                                {isDefaulting ? 'Setting…' : 'Make default'}
                            </button>
                        )}
                        {onChargeCardOnFile && !isConfirming && (
                            <button
                                type="button"
                                onClick={() => onChargeCardOnFile(m)}
                                style={primaryButton}
                                disabled={chargingVaultId === m.id || !!chargingVaultId}
                            >
                                {chargingVaultId === m.id ? 'Submitting…' : chargeButtonLabel}
                            </button>
                        )}
                        {!isConfirming && (
                            <button
                                type="button"
                                onClick={() => setConfirmId(m.id)}
                                style={ghostButton}
                                disabled={isDeleting || isDefaulting}
                            >
                                Remove
                            </button>
                        )}
                        {isConfirming && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(m.id)}
                                    disabled={isDeleting}
                                    style={dangerButton}
                                >
                                    {isDeleting ? 'Removing…' : 'Confirm remove'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConfirmId(null)}
                                    disabled={isDeleting}
                                    style={ghostButton}
                                >
                                    Cancel
                                </button>
                            </>
                        )}
                    </div>
                );
            })}

            {error && (
                <div style={{ color: '#b3261e', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}
        </div>
    );
}

const primaryButton = {
    padding: '6px 12px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
};

const ghostButton = {
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#586473',
    border: '1px solid #c4ccd6',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
};

const dangerButton = {
    padding: '6px 12px',
    backgroundColor: '#b3261e',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
};

const linkButton = {
    padding: 0,
    backgroundColor: 'transparent',
    color: '#1976d2',
    border: 'none',
    fontSize: 13,
    cursor: 'pointer',
};

const typeBadge = (label) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: '1px 7px',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    // Credit cards get a subtle amber tint as a visual cue that they
    // may incur a surcharge; debit reads as neutral teal so the patient
    // can scan their list and pick the lower-cost option at a glance.
    backgroundColor: label === 'Credit' ? '#fff4e0' : '#e6f6f3',
    color: label === 'Credit' ? '#a05a00' : '#0e7c66',
    border: `1px solid ${label === 'Credit' ? '#ffd28a' : '#b6e5dc'}`,
});

const surchargeNote = {
    color: '#a05a00',
    fontSize: 12,
    marginTop: 4,
    fontWeight: 500,
};
